import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, Clock } from "lucide-react";
import Logo from "./Logo";
import { COMPANY } from "../lib/api";
import { useLang } from "../contexts/LangContext";

export default function Footer() {
  const { t } = useLang();
  return (
    <footer className="bg-black border-t border-white/10 pt-20 pb-10" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4 space-y-5">
            <Logo className="h-32 w-auto" />
            <p className="text-zinc-400 text-sm leading-relaxed max-w-xs">{t("ft.tagline")}</p>
            <p className="text-zinc-500 text-xs leading-relaxed max-w-xs">Autobedrijf in Arnhem Noord. Ook actief in Velp, Oosterbeek, Westervoort, Duiven, Huissen, Elst en Zevenaar.</p>
            <div className="flex items-center gap-2 text-sm text-zinc-300">
              <span className="text-[#e60000] font-bold">★ {COMPANY.rating}</span>
              <span className="text-zinc-500">/ 5 — {COMPANY.reviews_count} reviews</span>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-eyebrow mb-5">{t("ft.nav")}</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/occasions" className="text-zinc-300 hover:text-white">{t("nav.occasions")}</Link></li>
              <li><Link to="/diensten" className="text-zinc-300 hover:text-white">{t("nav.diensten")}</Link></li>
              <li><Link to="/over-ons" className="text-zinc-300 hover:text-white">{t("nav.over")}</Link></li>
              <li><Link to="/reviews" className="text-zinc-300 hover:text-white">{t("nav.reviews")}</Link></li>
              <li><Link to="/inruil" className="text-zinc-300 hover:text-white">{t("ft.inruil")}</Link></li>
              <li><Link to="/afspraak" className="text-zinc-300 hover:text-white">{t("ft.afspraak")}</Link></li>
              <li><Link to="/nieuws" className="text-zinc-300 hover:text-white">Tips</Link></li>
            </ul>
          </div>

          <div className="md:col-span-5 space-y-4">
            <h4 className="text-eyebrow mb-5">{t("ft.contact")}</h4>
            <div className="space-y-3 text-sm">
              <a href="https://maps.google.com/?q=Johannes+Huddeweg+14+Arnhem" target="_blank" rel="noreferrer"
                 className="flex items-start gap-3 text-zinc-300 hover:text-white" data-testid="footer-address">
                <MapPin size={18} className="text-[#e60000] mt-0.5 shrink-0" />
                <span>{COMPANY.street}<br/>{COMPANY.postal}, {COMPANY.city}</span>
              </a>
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 text-zinc-300 hover:text-white" data-testid="footer-email">
                <Mail size={18} className="text-[#e60000]" />
                <span>{COMPANY.email}</span>
              </a>
              <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 text-zinc-300 hover:text-white" data-testid="footer-phone">
                <Phone size={18} className="text-[#e60000]" />
                <span>{COMPANY.phone}</span>
              </a>
              <div className="flex items-start gap-3 text-zinc-300">
                <Clock size={18} className="text-[#e60000] mt-0.5" />
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                  {COMPANY.hoursKeys.map((h) => (
                    <span key={h.key} className="contents">
                      <span className="text-zinc-400">{t(h.key)}</span>
                      <span>{h.timeKey ? t(h.timeKey) : h.time}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="red-line my-12" />

        <div className="mb-8" data-testid="footer-landing-links">
          <h4 className="text-eyebrow mb-4">{t("ft.services") || "Diensten in Arnhem"}</h4>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <Link to="/apk-arnhem" className="text-zinc-400 hover:text-white">APK Arnhem</Link>
            <Link to="/onderhoud-arnhem" className="text-zinc-400 hover:text-white">Onderhoud Arnhem</Link>
            <Link to="/airco-service-arnhem" className="text-zinc-400 hover:text-white">Airco service Arnhem</Link>
            <Link to="/auto-import-arnhem" className="text-zinc-400 hover:text-white">Auto import Arnhem</Link>
            <Link to="/dsg-specialist-arnhem" className="text-zinc-400 hover:text-white">DSG specialist Arnhem</Link>
            <Link to="/remmen-vervangen-arnhem" className="text-zinc-400 hover:text-white">Remmen vervangen Arnhem</Link>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between gap-4 text-xs text-zinc-500 uppercase tracking-wider">
          <div>© {new Date().getFullYear()} MP Autobedrijf — {t("common.allRights")}</div>
          <div className="flex flex-wrap gap-6">
            <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/voorwaarden" className="hover:text-white" data-testid="footer-voorwaarden-link">Voorwaarden</Link>
            <Link to="/admin/login" className="hover:text-white" data-testid="footer-admin-link">{t("nav.beheer")}</Link>
          </div>
        </div>
        <div className="mt-4 text-[11px] text-zinc-600 uppercase tracking-wider text-center sm:text-left">
          Autobedrijf Arnhem · Occasions Arnhem · APK Arnhem · Auto onderhoud & reparatie Arnhem
        </div>
      </div>
    </footer>
  );
}
